import { renderToStaticMarkup } from "react-dom/server";
import { AppType, DataPoints, getCitationFilePath } from "../../api";
import store from "../../Store";
import { normalizeAttachmentName, safeDecodeURIComponent } from "../../utils/AttachmentNameUtils";

export type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
    dataPoints: Array<DataPoints>;
};

export type AttachmentCitationInfo = {
    fileName: string;
    page?: number;
    displayText: string;
    token: string;
};

const ATTACHMENT_CITATION_PREFIX = "attachment:";
const ATTACHMENT_PAGE_CAPTURE_PATTERN = String.raw`(?:\s*(?:[#?][^\s<>()[\]]*?)?\b(?:page|pg|p)\s*=?\s*(\d+)|\s*\(?\s*(?:page|pg|p)\.?\s*[:=]?\s*(\d+)\s*\)?)?`;

type DataPoint = {
    sourcepage_field: string;
    uploaddir_field: string;
    doctype?: string;
    doc_url?: string;
};

type FileMappingObject = Record<string, unknown>;

export function isAttachmentCitationToken(citation: string): boolean {
    return citation.startsWith(ATTACHMENT_CITATION_PREFIX);
}

export function parseAttachmentCitationToken(citation: string): AttachmentCitationInfo | null {
    if (!isAttachmentCitationToken(citation)) return null;
    const raw = citation.slice(ATTACHMENT_CITATION_PREFIX.length);
    const parsed = extractAttachmentCitationFromText(raw);
    if (!parsed) return null;

    const token = buildAttachmentCitationToken(parsed.fileName, parsed.page);
    return {
        fileName: parsed.fileName,
        page: parsed.page,
        displayText: formatAttachmentCitationDisplay(parsed.fileName, parsed.page),
        token
    };
}

export function parseAnswerToHtml(
    answer: string,
    isStreaming: boolean,
    onCitationClicked: (citationFilePath: string, citationIndex: number) => void,
    dataPoints: DataPoint[],
    file_mapping: unknown,
    appType: AppType,
    sourcePage: "Chat" | "Feedback"
): HtmlParsedAnswer {
    // 1) Clean base text
    let parsedAnswer = answer.trim();

    // 1a) Keep attachment tags as citation-like bracket tokens so we can number them in answer body
    parsedAnswer = normalizeAttachmentTagsToBracketCitations(parsedAnswer);
    parsedAnswer = normalizeMalformedAttachmentMarkdownLinkSyntax(parsedAnswer);

    // 1b) file_mapping expansion
    if (file_mapping && typeof file_mapping === "object") {
        for (const key of Object.keys(file_mapping)) {
            const rawMappingValue = (file_mapping as FileMappingObject)[key];
            const resolvedMapping = resolveFileMappingText(rawMappingValue);
            if (!resolvedMapping) continue;

            const isAttachmentMapping = isAttachmentFileMappingEntry(key, resolvedMapping, rawMappingValue);
            if (isAttachmentMapping) {
                const mappedAttachment = extractAttachmentCitationFromText(resolvedMapping, getPageFromFileMappingValue(rawMappingValue));
                if (mappedAttachment) {
                    parsedAnswer = replaceAttachmentKeyWithCitationTokens(parsedAnswer, key, mappedAttachment);
                    continue;
                }
            }

            parsedAnswer = parsedAnswer.replaceAll(`[${key}]`, `[${resolvedMapping}]`).replaceAll(`[<source>${key}</source>]`, `[${resolvedMapping}]`);
            const bareKey = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
            parsedAnswer = parsedAnswer.replace(bareKey, `[${resolvedMapping}]`);
        }
    }

    // 1c) Convert attachment links/path mentions into attachment citation tokens
    parsedAnswer = replaceInlineAttachmentMarkdownLinksWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceInlineAttachmentPathReferencesWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceInlineAttachmentHrefReferencesWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceDoubleBracketAttachmentReferences(parsedAnswer);
    parsedAnswer = normalizeMalformedOpenAttachmentTokenSyntax(parsedAnswer);

    parsedAnswer = normalizeCitations(parsedAnswer);

    // 3) During streaming, drop a dangling bracketed tail
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") break;
            if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    // 4) Build valid citations from session datapoints (FAQ/Manuals/ALL)
    const overAllCurrentSessionDataPoints: {
        FAQ: Array<DataPoints>;
        Manuals: Array<DataPoints>;
        ALL: Array<DataPoints>;
    } = sourcePage === "Chat" ? store.getState().chatComponent.overAllCurrentSessionDataPoints : store.getState().feedBackPage.overAllCurrentSessionDataPoints;

    const appTypeDataPoints: Array<DataPoints> =
        appType === "ALL"
            ? overAllCurrentSessionDataPoints.ALL?.flat()
            : appType === "FAQ"
              ? overAllCurrentSessionDataPoints.FAQ?.flat()
              : overAllCurrentSessionDataPoints.Manuals?.flat();

    const validCitations = new Set(
        appTypeDataPoints.flatMap(item => [
            `${item.uploaddir_field}/${item.sourcepage_field}`,
            `${item.uploaddir_field}/`, // kept to match your original behavior; we’ll ignore trailing '/' later
            `${item.sourcepage_field}`
        ])
    );

    const knownSourceCitationFileNames = new Set(
        Array.from(validCitations)
            .map(citation => normalizeCitationFileNameForComparison(citation))
            .filter(Boolean)
    );
    const knownAttachmentFileNames = getKnownAttachmentFileNames(dataPoints);
    parsedAnswer = replaceBracketAttachmentReferencesWithCitationTokens(parsedAnswer, validCitations, knownSourceCitationFileNames, knownAttachmentFileNames);

    // 5) Tokenize text into [normalized citations] and plain text parts
    const parts = splitByValidCitationsSimple(parsedAnswer, validCitations, token => isAttachmentCitationToken(token));

    // 6) Collect citations by type first so source citations always come before attachment citations.
    const sourceCitations: string[] = [];
    const attachmentCitations: string[] = [];
    const sourceCitationSet = new Set<string>();
    const attachmentCitationSet = new Set<string>();
    const sourceCitationFileNameSet = new Set<string>();

    const registerCitationToken = (token: string) => {
        const isAttachmentCitation = isAttachmentCitationToken(token);
        if (token.endsWith("/") || (!validCitations.has(token) && !isAttachmentCitation)) return;

        if (isAttachmentCitation) {
            const attachmentInfo = parseAttachmentCitationToken(token);
            if (shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo, sourceCitationFileNameSet)) {
                return;
            }
            if (!attachmentCitationSet.has(token)) {
                attachmentCitationSet.add(token);
                attachmentCitations.push(token);
            }
            return;
        }

        if (!sourceCitationSet.has(token)) {
            sourceCitationSet.add(token);
            sourceCitations.push(token);
            const sourceFileName = normalizeCitationFileNameForComparison(token);
            if (sourceFileName) {
                sourceCitationFileNameSet.add(sourceFileName);
            }
        }
    };

    for (const part of parts) {
        const token = extractNormalizedCitation(part);
        if (!token) continue;
        registerCitationToken(token);
    }

    const fallbackAttachmentCitations = getAttachmentFallbackCitations(parsedAnswer, dataPoints);
    for (const fallbackToken of fallbackAttachmentCitations) {
        const fallbackInfo = parseAttachmentCitationToken(fallbackToken);
        if (shouldSkipAttachmentCitationDueToSourceCollision(fallbackInfo, sourceCitationFileNameSet)) {
            continue;
        }
        const hasSameAttachmentAlready = attachmentCitations.some(existingToken => {
            const existingInfo = parseAttachmentCitationToken(existingToken);
            return (
                !!fallbackInfo &&
                !!existingInfo &&
                existingInfo.fileName.toLowerCase() === fallbackInfo.fileName.toLowerCase() &&
                (existingInfo.page === fallbackInfo.page || Number.isFinite(existingInfo.page))
            );
        });
        if (!hasSameAttachmentAlready && !attachmentCitationSet.has(fallbackToken)) {
            attachmentCitationSet.add(fallbackToken);
            attachmentCitations.push(fallbackToken);
        }
    }

    const filteredAttachmentCitations = attachmentCitations.filter(token => {
        const attachmentInfo = parseAttachmentCitationToken(token);
        return !shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo, sourceCitationFileNameSet);
    });

    const citations: string[] = [...sourceCitations, ...filteredAttachmentCitations];
    const citationIndexMap = new Map<string, number>(citations.map((token, index) => [token, index]));
    const sourceCitationIndexMap = new Map<string, number>(sourceCitations.map((token, index) => [token, index + 1]));

    // 7) Render: valid citations -> numbered <sup>; everything else -> text
    const fragments: string[] = parts.map(part => {
        const token = extractNormalizedCitation(part);
        if (!token) {
            return part.endsWith("/") ? "" : part; // keep your trailing-slash guard
        }

        const isAttachmentCitation = isAttachmentCitationToken(token);
        if (token.endsWith("/") || (!validCitations.has(token) && !isAttachmentCitation)) {
            return `[${token}]`;
        }

        const idx = citationIndexMap.get(token);
        if (idx === undefined) {
            return `[${token}]`;
        }

        if (isAttachmentCitation) {
            const attachmentInfo = parseAttachmentCitationToken(token);
            return renderToStaticMarkup(
                <a className="supContainer" title={attachmentInfo?.displayText ?? token}>
                    <sup>{idx + 1}</sup>
                </a>
            );
        }

        const path = getCitationFilePath(token);
        const supportingCitationIndex = sourceCitationIndexMap.get(token) ?? idx + 1;
        return renderToStaticMarkup(
            <a className="supContainer" title={token} onClick={() => onCitationClicked(path, supportingCitationIndex)}>
                <sup>{idx + 1}</sup>
            </a>
        );
    });

    return {
        answerHtml: fragments.join(""),
        citations,
        dataPoints: appTypeDataPoints
    };
}

/**
 * Normalize/emit ONLY valid citations as separate parts.
 * Accepts:
 *   [<source>file</source>]
 *   [file]
 *   [<source>[file]</source>]
 *   or any wrapper like [foo <source>[file]</source> bar] -> extracts inner [file] if valid
 * Returns an array mixing:
 *   - plain text chunks
 *   - normalized citation chunks as "[token]" (no <source> wrappers)
 *
 * @internal Exported to support unit testing of citation parsing logic.
 * This is an implementation detail of the answer parsing pipeline and should not be
 * used directly by external consumers. Use parseAnswerToHtml() as the public API.
 */
export function splitByValidCitationsSimple(input: string, valid: Set<string>, isExtraValidToken?: (token: string) => boolean): string[] {
    const out: string[] = [];
    let i = 0,
        buf = "";

    const flush = () => {
        if (buf) {
            out.push(buf);
            buf = "";
        }
    };
    while (i < input.length) {
        if (input[i] === "[") {
            // try to find closing ]
            const j = input.indexOf("]", i + 1);
            if (j !== -1) {
                const inside = input.slice(i + 1, j).trim();
                if (valid.has(inside) || isExtraValidToken?.(inside)) {
                    flush();
                    out.push(`[${inside}]`);
                    i = j + 1;
                    continue;
                }

                // not valid -> keep as text
            }
        }
        buf += input[i++];
    }
    flush();
    return out;
}

/** If part is a normalized citation like "[token]", return "token"; otherwise null */
export function extractNormalizedCitation(part: string): string | null {
    const matched = part.match(/^\[\s*([^\]]+?)\s*\]$/);
    return matched ? matched[1].trim() : null;
}

export function normalizeCitations(text: string): string {
    let prev: string;
    do {
        prev = text;
        // Case A: [ <source> filename </source> ]  -> [filename]
        text = text.replace(/\[\s*<\s*source\s*>\s*([\s\S]*?)\s*<\s*\/\s*source\s*>\s*\]/gi, (_, p1) => `[${p1.trim()}]`);
        // Case B: double brackets [[ filename ]] -> [filename]
        text = text.replace(/\[\s*\[\s*([^\]]+?)\s*\]\s*\]/g, (_, p1) => `[${p1.trim()}]`);
        // Case C: trim spaces inside single brackets [  token  ] -> [token]
        text = text.replace(/\[\s*([^\]]+?)\s*\]/g, (_, p1) => `[${p1.trim()}]`);
    } while (text !== prev); // iterate until stable
    return text;
}

function cleanAttachmentDisplayName(value?: string): string {
    return normalizeAttachmentName(value, {
        lowerCase: false,
        trimTrailingPunctuation: true,
        trailingPunctuationPattern: /[.,;:]+$/
    });
}

function hasFileExtension(value: string): boolean {
    return /\.[a-z0-9]{1,10}$/i.test(value);
}

function normalizeCitationFileNameForComparison(value?: string): string {
    if (!value) return "";
    const normalizedFileName = cleanAttachmentDisplayName(value).toLowerCase();
    return hasFileExtension(normalizedFileName) ? normalizedFileName : "";
}

function shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo: AttachmentCitationInfo | null, sourceCitationFileNameSet: Set<string>): boolean {
    if (!attachmentInfo || Number.isFinite(attachmentInfo.page)) return false;
    const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachmentInfo.fileName);
    if (!normalizedAttachmentName) return false;
    return sourceCitationFileNameSet.has(normalizedAttachmentName);
}

function normalizeAttachmentTagsToBracketCitations(text: string): string {
    let next = text;
    next = next.replace(/\[\s*<\s*attachment\s*>\s*([\s\S]*?)\s*<\s*\/\s*attachment\s*>\s*\]/gi, (_m, p1) => `[${String(p1).trim()}]`);
    next = next.replace(/<\s*attachment\s*>\s*([\s\S]*?)\s*<\s*\/\s*attachment\s*>/gi, (_m, p1) => `[${String(p1).trim()}]`);
    // Recover malformed angle wrappers like: [<[[file.pdf#page=4]>]
    next = next.replace(/\[\s*<\s*\[\[([\s\S]*?)\]\s*>\s*\]/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    next = next.replace(/<\s*\[\[([\s\S]*?)\]\s*>/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    // Recover malformed outputs like: [<[[file.pdf#page=6]</attachment>]
    next = next.replace(/\[\s*<\s*\[\[([\s\S]*?)\]\s*<\s*\/\s*attachment\s*>\s*\]/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    // Recover malformed outputs where the opening <attachment> tag is missing.
    next = next.replace(/\]\s*<\s*\/\s*attachment\s*>\s*\]/gi, "]]");
    next = next.replace(/<\s*\/\s*attachment\s*>/gi, "");
    return next;
}

function normalizeMalformedAttachmentMarkdownLinkSyntax(text: string): string {
    let next = text;
    // Recover malformed markdown links like: [[file]]([[file]) to avoid duplicate/garbled captures.
    next = next.replace(/\]\]\s*\(\s*\[\[([\s\S]*?)\]\)?\s*\)/gi, "]]");
    return next;
}

function recoverLikelyMissingLeadingBracket(value: string): string {
    const normalized = value.trim();
    if (!normalized) return normalized;
    const firstClosingBracket = normalized.indexOf("]");
    const firstOpeningBracket = normalized.indexOf("[");
    if (firstClosingBracket > 0 && (firstOpeningBracket === -1 || firstOpeningBracket > firstClosingBracket)) {
        return `[${normalized}`;
    }
    return normalized;
}

function resolveFileMappingText(value: unknown): string {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }
    if (!value || typeof value !== "object") return "";

    const objectValue = value as Record<string, unknown>;
    const uploadDir = String(objectValue.uploaddir_field ?? objectValue.uploadDir ?? "").trim();
    const sourcePage = String(objectValue.sourcepage_field ?? objectValue.sourcePage ?? objectValue.sourcepage ?? "").trim();
    if (uploadDir && sourcePage) {
        return `${uploadDir}/${sourcePage}`;
    }

    const candidateFields = [
        "path",
        "blob_path",
        "blobPath",
        "source",
        "sourcepage",
        "sourcePage",
        "citation",
        "value",
        "url",
        "href",
        "doc_url",
        "docUrl",
        "file_name",
        "fileName",
        "name",
        "filename"
    ];
    for (const field of candidateFields) {
        const candidate = objectValue[field];
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }
    }
    return "";
}

function getPageFromFileMappingValue(value: unknown): number | undefined {
    if (!value || typeof value !== "object") return undefined;
    const objectValue = value as Record<string, unknown>;
    const pageCandidates = [objectValue.page, objectValue.page_no, objectValue.pageNo, objectValue.page_number, objectValue.pageNumber];
    for (const pageCandidate of pageCandidates) {
        const pageNumber = Number(pageCandidate);
        if (Number.isFinite(pageNumber)) {
            return pageNumber;
        }
    }
    return undefined;
}

function isAttachmentFileMappingEntry(key: string, resolvedValue: string, rawValue: unknown): boolean {
    const keyLower = key.toLowerCase();
    const valueLower = resolvedValue.toLowerCase();
    if (keyLower.startsWith("attachment")) return true;
    if (valueLower.includes("attachments/") || valueLower.startsWith("attachment://") || valueLower.includes("/attachments/")) return true;

    if (rawValue && typeof rawValue === "object") {
        const objectValue = rawValue as Record<string, unknown>;
        const typeHints = [objectValue.type, objectValue.doctype, objectValue.category, objectValue.source_type, objectValue.sourceType]
            .map(item => String(item ?? "").toLowerCase())
            .filter(Boolean);
        if (typeHints.some(item => item.includes("attachment") || item.includes("user file"))) {
            return true;
        }
    }
    return false;
}

function parseOptionalPage(pageA?: string, pageB?: string): number | undefined {
    const pageValue = pageA || pageB;
    if (!pageValue) return undefined;
    const parsed = Number(pageValue);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function buildAttachmentCitationToken(fileName: string, page?: number): string {
    const cleanedFileName = cleanAttachmentDisplayName(fileName);
    if (!cleanedFileName) return "";
    const encodedFileName = encodeURIComponent(cleanedFileName);
    if (Number.isFinite(page)) {
        return `${ATTACHMENT_CITATION_PREFIX}${encodedFileName}#page=${page}`;
    }
    return `${ATTACHMENT_CITATION_PREFIX}${encodedFileName}`;
}

function formatAttachmentCitationDisplay(fileName: string, page?: number): string {
    const normalizedName = cleanAttachmentDisplayName(fileName);
    if (Number.isFinite(page)) {
        return `${normalizedName} (page=${page})`;
    }
    return normalizedName;
}

function extractAttachmentCitationFromText(rawValue?: string, forcedPage?: number): { fileName: string; page?: number } | null {
    if (!rawValue) return null;
    let candidate = safeDecodeURIComponent(String(rawValue)).trim();
    if (!candidate) return null;
    candidate = candidate.replace(/^<+/, "").replace(/>+$/, "").trim();
    candidate = normalizeMalformedAttachmentCandidate(candidate);

    if (candidate.toLowerCase().includes("attachment://")) {
        candidate = candidate.replace(/^.*?attachment:\/\//i, "");
    } else {
        const attachmentsStart = candidate.toLowerCase().indexOf("attachments/");
        if (attachmentsStart >= 0) {
            candidate = candidate.slice(attachmentsStart);
        }
    }

    candidate = candidate.replace(/[.,;:'"]+\s*$/, "").trim();

    let page: number | undefined = Number.isFinite(forcedPage) ? forcedPage : undefined;
    const pageMatch =
        candidate.match(/[?#][^#?]*\b(?:page|pg|p)\s*=?\s*(\d+)\s*[\])>]*$/i) ||
        candidate.match(/\(\s*(?:page|pg|p)\s*[:=]?\s*(\d+)\s*\)\s*[\])>]*$/i) ||
        candidate.match(/\b(?:page|pg|p)\s*[:=]?\s*(\d+)\s*[\])>]*$/i);

    if (!page && pageMatch?.[1]) {
        const parsed = Number(pageMatch[1]);
        if (Number.isFinite(parsed)) {
            page = parsed;
        }
    }

    if (pageMatch?.[0]) {
        candidate = candidate.replace(pageMatch[0], "").trim();
    }

    candidate = candidate.replace(/[)\],.;:'"]+$/, "").trim();
    candidate = candidate.replace(/^attachments\//i, "").trim();
    const candidateSegments = candidate.split("/").filter(Boolean);
    const fileNameCandidate = candidateSegments.length > 0 ? candidateSegments[candidateSegments.length - 1] : candidate;
    const fileName = normalizeAttachmentFileName(fileNameCandidate);
    if (!fileName || !hasFileExtension(fileName)) return null;

    return {
        fileName,
        page: Number.isFinite(page) ? page : undefined
    };
}

function normalizeMalformedAttachmentCandidate(value: string): string {
    let normalized = value.trim();
    // Merge concatenated malformed fragments from markdown links.
    normalized = normalized.replace(/\]\]\s*\(\s*\[\[/g, "[[");
    normalized = normalized.replace(/\]\)\s*\[\[/g, "[[");
    normalized = normalized.replace(/\]\s*\[\[/g, "[[");

    const lastDoubleBracketIndex = normalized.lastIndexOf("[[");
    if (lastDoubleBracketIndex > 0) {
        normalized = normalized.slice(lastDoubleBracketIndex + 1);
    }
    return normalized.trim();
}

function normalizeAttachmentFileName(value: string): string {
    let normalized = cleanAttachmentDisplayName(value);
    normalized = normalized.replace(/^\[\[/, "[");
    normalized = recoverLikelyMissingLeadingBracket(normalized);
    return normalized;
}

function replaceAttachmentKeyWithCitationTokens(answer: string, key: string, mappedAttachment: { fileName: string; page?: number }): string {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const replacement = (pageA?: string, pageB?: string): string => {
        const overridePage = parseOptionalPage(pageA, pageB);
        const token = buildAttachmentCitationToken(mappedAttachment.fileName, Number.isFinite(overridePage) ? overridePage : mappedAttachment.page);
        return token ? `[${token}]` : `[${mappedAttachment.fileName}]`;
    };

    let parsedAnswer = answer;
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`\\[\\s*<\\s*attachment\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*attachment\\s*>\\s*\\]`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`<\\s*attachment\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*attachment\\s*>`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`\\[\\s*<\\s*source\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*source\\s*>\\s*\\]`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(new RegExp(`\\[\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*\\]`, "gi"), (_m, _k, pageA, pageB) =>
        replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`(^|[^a-zA-Z0-9_])(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}`, "gi"),
        (_m, prefix, _k, pageA, pageB) => {
            return `${prefix}${replacement(pageA, pageB)}`;
        }
    );
    return parsedAnswer;
}

function replaceInlineAttachmentMarkdownLinksWithCitationTokens(answer: string): string {
    return answer.replace(/\[(?:\\.|[^\\\]])*?\]\((attachment:\/\/[^\s)]+)\)/gi, (match: string, href: string) => {
        const attachment = extractAttachmentCitationFromText(href);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceInlineAttachmentPathReferencesWithCitationTokens(answer: string): string {
    const attachmentPathRegex =
        /attachments\/[^<>\r\n\t]+(?:\.[a-z0-9]{1,10})+(?:\s*\(?\s*(?:page|pg|p)\.?\s*[:=]?\s*\d+\s*\)?|[?#][^\s<>()[\]]*\b(?:page|pg|p)\s*=?\s*\d+)?/gi;
    return answer.replace(attachmentPathRegex, (match: string) => {
        const attachment = extractAttachmentCitationFromText(match);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceInlineAttachmentHrefReferencesWithCitationTokens(answer: string): string {
    return answer.replace(/attachment:\/\/[^\s)\]]+/gi, (match: string) => {
        const attachment = extractAttachmentCitationFromText(match);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceDoubleBracketAttachmentReferences(answer: string): string {
    return answer.replace(/\[\[([\s\S]*?)\]\]/g, (match: string, inner: string) => {
        const attachment = extractAttachmentCitationFromText(inner);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function normalizeMalformedOpenAttachmentTokenSyntax(answer: string): string {
    let next = answer;
    // Recover malformed link label remnants like: "[Open ... [attachment:...]" -> "Open ... [attachment:...]"
    next = next.replace(/\[\s*(Open[^[]*?)\s*(?=\[\s*attachment:[^\]]+\])/gi, (_m, label) => `${String(label).trim()} `);
    // Remove stray "(" before attachment token: "Open the paper ([attachment:...]" -> "Open the paper [attachment:...]"
    next = next.replace(/(Open[^[]*?)\(\s*(?=\[\s*attachment:[^\]]+\])/gi, (_m, label) => `${String(label).trim()} `);
    // Collapse consecutive duplicate attachment tokens produced by malformed markdown link tails.
    next = next.replace(/\[\s*(attachment:[^\]]+)\s*\]\s+\[\s*\1\s*\]/gi, "[$1]");
    return next;
}

function replaceBracketAttachmentReferencesWithCitationTokens(
    answer: string,
    validCitations: Set<string>,
    knownSourceCitationFileNames: Set<string>,
    knownAttachmentFileNames: Set<string>
): string {
    return answer.replace(/\[([^\]\r\n]+)\]/g, (match: string, inner: string) => {
        const token = String(inner ?? "").trim();
        if (!token || isAttachmentCitationToken(token)) return match;
        if (/<\s*\/?\s*(source|attachment)\s*>/i.test(token)) return match;

        // Check if this token resolves to a known attachment file name.
        // Known attachment files must always become attachment citation tokens,
        // even when their bare filename also appears in validCitations (which
        // happens because user-file data points contribute to validCitations
        // via sourcepage_field). Without this priority check, bare filenames
        // like [report.docx] would be treated as source citations, colliding
        // with the attachment:report.docx tokens and causing both rendering
        // failures and a spurious Sources-tab button.
        const attachment = extractAttachmentCitationFromText(token);
        if (attachment) {
            const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachment.fileName);
            if (normalizedAttachmentName && knownAttachmentFileNames.has(normalizedAttachmentName)) {
                const attachmentToken = buildAttachmentCitationToken(attachment.fileName, attachment.page);
                return attachmentToken ? `[${attachmentToken}]` : match;
            }
        }

        // Not a known attachment file – skip valid source citations
        if (validCitations.has(token)) return match;

        const tokenLower = token.toLowerCase();
        const hasSlash = token.includes("/") || token.includes("\\");
        const hasAttachmentPathHint = tokenLower.includes("attachments/") || tokenLower.startsWith("attachment://") || tokenLower.includes("/attachments/");
        if (hasSlash && !hasAttachmentPathHint) return match;

        if (!attachment) return match;

        const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachment.fileName);
        if (!normalizedAttachmentName) return match;
        if (!hasAttachmentPathHint) return match;
        if (!Number.isFinite(attachment.page) && knownSourceCitationFileNames.has(normalizedAttachmentName)) return match;

        const attachmentToken = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return attachmentToken ? `[${attachmentToken}]` : match;
    });
}

function isAttachmentDataPoint(point: DataPoint): boolean {
    const uploadDir = (point?.uploaddir_field ?? "").toString().toLowerCase();
    const sourcePage = (point?.sourcepage_field ?? "").toString().toLowerCase();
    const docType = (point?.doctype ?? "").toString().toLowerCase();
    const docUrl = (point?.doc_url ?? "").toString().toLowerCase();
    return (
        uploadDir.startsWith("attachments/") ||
        sourcePage.includes("attachments/") ||
        uploadDir === "user_files" ||
        docType === "user file" ||
        docUrl.includes("/attachments/")
    );
}

function getKnownAttachmentFileNames(dataPoints: DataPoint[]): Set<string> {
    const fileNames = new Set<string>();
    if (!Array.isArray(dataPoints)) return fileNames;

    for (const point of dataPoints) {
        if (!isAttachmentDataPoint(point)) continue;
        const uploadDir = (point?.uploaddir_field ?? "").toString();
        const sourcePage = (point?.sourcepage_field ?? "").toString();
        const fileName = normalizeCitationFileNameForComparison(sourcePage || uploadDir);
        if (fileName) {
            fileNames.add(fileName);
        }
    }
    return fileNames;
}

function getAttachmentFallbackCitations(answer: string, dataPoints: DataPoint[]): string[] {
    if (!answer || !Array.isArray(dataPoints) || dataPoints.length === 0) return [];
    const answerLower = answer.toLowerCase();
    const tokens: string[] = [];
    const seen = new Set<string>();

    for (const point of dataPoints) {
        const uploadDir = (point?.uploaddir_field ?? "").toString();
        const sourcePage = (point?.sourcepage_field ?? "").toString();
        if (!isAttachmentDataPoint(point)) continue;

        const fileName = cleanAttachmentDisplayName(sourcePage || uploadDir);
        if (!fileName || !hasFileExtension(fileName)) continue;
        const encodedFileName = encodeURIComponent(fileName).toLowerCase();
        const normalizedFileName = fileName.toLowerCase();
        if (!answerLower.includes(normalizedFileName) && !answerLower.includes(encodedFileName)) continue;

        const token = buildAttachmentCitationToken(fileName);
        if (!token || seen.has(token)) continue;
        seen.add(token);
        tokens.push(token);
    }

    return tokens;
}
